/**
 * 滚动驱动的相机轨道控制器。
 * 它不渲染可见物体，而是在每帧把 ScrollControls 的 0～1 进度换算成章节、
 * 相机位置和注视点，并将当前章节写回 Recoil 供 DOM 层使用。
 */
import { useScroll } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { memo, useMemo, useRef } from 'react';
import { Mesh, Vector3 } from 'three';
import { useScreensState } from '../../atoms/screens';
import { data } from '../../data';
import { cameraLookAtVec } from '../../lookAtVec';

/** 标准线性插值：frac=0 得到 a，frac=1 得到 b。 */
const interpolate = (a: number, b: number, frac: number) => a + (b - a) * frac;

// 触屏设备使用略强的 lerp 系数，使手指滚动后的相机更快跟上。
const isTouch = matchMedia('(pointer: coarse)').matches;
// 最后一章没有下一条 data，使用设计好的收尾坐标完成最后一段移动。
const fallbackFrom = { x: 72.53, y: 20.34, z: 5.53 };
const fallback = data[1];

const FlyingCamera = () => {
    // ghostMesh 是不可见的“注视目标”；让相机 lookAt 一个对象比散落坐标更易调试。
    const ghostMesh = useRef<Mesh>(null);

    const scroll = useScroll();
    // 暴露给菜单跳转和圆环进度。它们在 DOM 层，无法直接调用 useScroll。
    (window as any)._scrollEl = scroll.el;
    const [currentScreen, setCurrentScreen] = useScreensState();
    // 重用 Vector3，避免 60FPS 下持续创建临时对象给垃圾回收器。
    const cameraPosVec = useMemo(() => new Vector3(), []);

    useFrame((state) => {
        // offset 是整个体验的归一化滚动比例 0～1。
        const scrollOffset = scroll.offset;
        (window as any)._scrollOffset = scrollOffset;

        // 找出 offset 落在哪个等分章节区间。
        for (let i = 0; i < scroll.pages; i++) {
            if (
                scrollOffset >= i / scroll.pages &&
                scrollOffset < (i + 1) / scroll.pages
            ) {
                if (data[i + 1]) {
                    if (currentScreen !== i + 1) {
                        setCurrentScreen(i + 1);
                    }

                    // range 把本章节内部进度再次归一化为 0～1。
                    const range = scroll.range(
                        i / scroll.pages,
                        1 / scroll.pages,
                    );

                    // fallbackFrom

                    // 先算章节间目标坐标，再用 lerp 对每一帧做阻尼追随，形成飞行感。
                    state.camera.position.lerp(
                        cameraPosVec.set(
                            interpolate(
                                data[i + 1].from.x,
                                // (data[i + 2] || fallback).from.x,
                                (data[i + 2] ? data[i + 2].from : fallbackFrom)
                                    .x,
                                range,
                            ),
                            interpolate(
                                data[i + 1].from.y,
                                // (data[i + 2] || fallback).from.y,
                                (data[i + 2] ? data[i + 2].from : fallbackFrom)
                                    .y,
                                range,
                            ),
                            interpolate(
                                data[i + 1].from.z,
                                // (data[i + 2] || fallback).from.z,
                                (data[i + 2] ? data[i + 2].from : fallbackFrom)
                                    .z,
                                range,
                            ),
                        ),
                        isTouch ? 0.1 : 0.08,
                    );

                    if (ghostMesh.current) {
                        // 注视点独立插值，因此相机既平移又平滑转头。
                        ghostMesh.current.position.lerp(
                            cameraLookAtVec.set(
                                interpolate(
                                    data[i + 1].cameraLookAt.x,
                                    (data[i + 2] || fallback).cameraLookAt.x,
                                    range,
                                ),
                                interpolate(
                                    data[i + 1].cameraLookAt.y,
                                    (data[i + 2] || fallback).cameraLookAt.y,
                                    range,
                                ),
                                interpolate(
                                    data[i + 1].cameraLookAt.z,
                                    (data[i + 2] || fallback).cameraLookAt.z,
                                    range,
                                ),
                            ),
                            isTouch ? 0.1 : 0.06,
                        );

                        state.camera.lookAt(ghostMesh.current.position);
                    }
                }

                break;
            }
        }
    });

    return <mesh ref={ghostMesh} name="ghost" />;
};

export default memo(FlyingCamera);
